import React, { useState, useEffect, useRef } from 'react';
import { 
  Sparkles, Moon, Sun, Infinity as InfinityIcon, Gem, Loader2, Zap, 
  AlertCircle, Volume2, VolumeX, Atom, Hexagon, Triangle, CircleDashed, 
  Orbit, Flame, Info, X, Repeat, StopCircle, PlayCircle, Plus, Minus,
  Wind, Ghost, Wrench, Scroll
} from 'lucide-react';
import Confetti from 'react-confetti';
import { useAppContext } from '../AppContext';
import { doc, onSnapshot, collection, query, orderBy, limit } from 'firebase/firestore';

// --- Types & Constants ---

const SYMBOLS = {
  FLUX: { 
    id: 'FLUX', 
    icon: Wind, 
    color: 'text-slate-400', 
    glowColor: 'shadow-slate-500',
    bgGradient: 'from-slate-800/50 to-transparent',
    value: 2,
    weight: 120,
    description: 'Faint Whisper'
  },
  PRISM: { 
    id: 'PRISM', 
    icon: CircleDashed, 
    color: 'text-cyan-300', 
    glowColor: 'shadow-cyan-400',
    bgGradient: 'from-cyan-900/50 to-transparent',
    value: 5, 
    weight: 80,
    description: 'Resonating Echo'
  },
  NEBULA: { 
    id: 'NEBULA', 
    icon: Hexagon, 
    color: 'text-indigo-400', 
    glowColor: 'shadow-indigo-500',
    bgGradient: 'from-indigo-900/50 to-transparent',
    value: 10,
    weight: 60,
    description: 'Mental Construct'
  },
  STAR: { 
    id: 'STAR', 
    icon: Ghost, 
    color: 'text-purple-300', 
    glowColor: 'shadow-purple-300',
    bgGradient: 'from-purple-900/50 to-transparent',
    value: 20,
    weight: 40,
    description: 'Wandering Soul'
  },
  ATOM: { 
    id: 'ATOM', 
    icon: Atom, 
    color: 'text-green-400', 
    glowColor: 'shadow-green-500',
    bgGradient: 'from-green-900/50 to-transparent',
    value: 40,
    weight: 25,
    description: 'Connection Point'
  },
  ENERGY: { 
    id: 'ENERGY', 
    icon: Zap, 
    color: 'text-orange-400', 
    glowColor: 'shadow-orange-500',
    bgGradient: 'from-orange-900/50 to-transparent',
    value: 80,
    weight: 15,
    description: 'Chaotic Energy'
  },
  VOID: { 
    id: 'VOID', 
    icon: InfinityIcon, 
    color: 'text-fuchsia-500', 
    glowColor: 'shadow-fuchsia-600',
    bgGradient: 'from-fuchsia-900/50 to-transparent',
    value: 200,
    weight: 8,
    description: 'The Void'
  },
  GEM: { 
    id: 'GEM', 
    icon: Gem, 
    color: 'text-pink-500', 
    glowColor: 'shadow-pink-500',
    bgGradient: 'from-pink-900/50 to-transparent',
    value: 500,
    weight: 2,
    description: 'Perfect Harmony'
  }
};

const SYMBOL_KEYS = Object.keys(SYMBOLS);

const PAYLINES = [
  { name: 'Top Orbit', coords: [[0,0], [1,0], [2,0]], color: 'bg-red-500' },
  { name: 'Equator', coords: [[0,1], [1,1], [2,1]], color: 'bg-yellow-400' },
  { name: 'Bottom Orbit', coords: [[0,2], [1,2], [2,2]], color: 'bg-blue-500' },
  { name: 'Descent', coords: [[0,0], [1,1], [2,2]], color: 'bg-purple-500' },
  { name: 'Ascent', coords: [[0,2], [1,1], [2,0]], color: 'bg-green-500' },
  { name: 'Left Pillar', coords: [[0,0], [0,1], [0,2]], color: 'bg-orange-500' },
  { name: 'Core Axis', coords: [[1,0], [1,1], [1,2]], color: 'bg-cyan-500' },
  { name: 'Right Pillar', coords: [[2,0], [2,1], [2,2]], color: 'bg-pink-500' },
];

const JACKPOT_SEED = 0;
const JACKPOT_VISUAL_CAP = 50000;
const BET_OPTIONS = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];

// --- Sub-Components ---

const SymbolCell = ({ symbolKey, highlighted, isNearMiss, dim }) => {
  const SymbolData = SYMBOLS[symbolKey];
  const Icon = SymbolData.icon;
  const isGem = symbolKey === 'GEM';

  return (
    <div className={`
      relative flex items-center justify-center w-full h-full rounded-xl
      transition-all duration-300 transform
      ${highlighted 
        ? `bg-gradient-to-br ${SymbolData.bgGradient} scale-105 z-20 ring-2 ring-white/50 shadow-[0_0_30px_rgba(255,255,255,0.2)]` 
        : `border border-transparent ${dim ? 'opacity-30 grayscale scale-90' : isNearMiss ? 'opacity-90 scale-100 border-white/10' : 'opacity-100 scale-100'}`
      }
      ${isNearMiss ? 'animate-nudge' : ''}
    `}>
      {!highlighted && !dim && SymbolData.value >= 20 && (
         <div className={`absolute inset-2 opacity-20 bg-${SymbolData.color.split('-')[1]}-500 blur-xl rounded-full`} />
      )}
      {isGem && !dim && !highlighted && (
         <div className="absolute inset-0 rounded-xl animate-pulse-gem-glow opacity-50" />
      )}
      {highlighted && (
        <div className="absolute inset-0 rounded-xl bg-white/20 animate-pulse mix-blend-overlay" />
      )}
      <Icon 
        className={`
          w-2/3 h-2/3 transition-all duration-300
          ${SymbolData.color} 
          ${highlighted ? 'drop-shadow-[0_0_20px_currentColor] scale-110' : 'drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]'}
        `}
        strokeWidth={highlighted ? 2.5 : 1.5}
      />
    </div>
  );
};

const Reel = ({ symbols, isSpinning, stopDelay, highlightIndices, nearMissIndices, hasWin }) => {
  const [visualState, setVisualState] = useState('stopped');

  useEffect(() => {
    if (isSpinning) {
      setVisualState('spinning');
    } else {
      const timer = setTimeout(() => {
        setVisualState('stopped');
      }, stopDelay);
      return () => clearTimeout(timer);
    }
  }, [isSpinning, stopDelay]);

  return (
    <div className="relative w-full h-full bg-slate-950/40 border-x border-white/5 overflow-hidden rounded-lg backdrop-blur-sm">
      {/* Inner Shadow for Depth */}
      <div className="absolute inset-0 pointer-events-none z-20 shadow-[inset_0_0_20px_rgba(0,0,0,0.8)]" />
      
      <div className="h-full relative z-10 py-2 px-1 flex flex-col justify-around gap-2">
        {visualState === 'spinning' ? (
          <div className="w-full h-full flex flex-col animate-spin-blur opacity-60 gap-4">
             {[1, 2, 3].map((i) => (
                <div key={i} className="flex-1 w-full flex items-center justify-center">
                    <div className="w-10/12 h-3/4 rounded-xl bg-gradient-to-b from-transparent via-white/5 to-transparent blur-md" />
                </div>
             ))}
          </div>
        ) : (
          <div className="h-full flex flex-col w-full gap-2 animate-reel-bounce">
            {symbols.map((sym, index) => (
               <div key={index} className="flex-1 w-full flex items-center justify-center h-[33%]">
                 <SymbolCell 
                   symbolKey={sym} 
                   highlighted={highlightIndices.includes(index)} 
                   isNearMiss={nearMissIndices.includes(index)}
                   dim={hasWin && !highlightIndices.includes(index) && !nearMissIndices.includes(index)}
                 />
               </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CommunityPool = ({ value, intensity, isExploding, contribution }) => {
  let coreColor = 'text-cyan-400 shadow-cyan-500';
  let ringColor = 'border-cyan-500/30';
  
  // Intensity levels
  const isMedium = intensity > 30;
  const isHigh = intensity > 60;
  const isCritical = intensity > 85;

  if (isMedium) { coreColor = 'text-purple-400 shadow-purple-500'; ringColor = 'border-purple-500/30'; }
  if (isHigh) { coreColor = 'text-fuchsia-400 shadow-fuchsia-500'; ringColor = 'border-fuchsia-500/30'; }
  if (isCritical) { coreColor = 'text-pink-500 shadow-pink-600'; ringColor = 'border-pink-500/50'; }

  const [prevValue, setPrevValue] = useState(value);
  const [justIncreased, setJustIncreased] = useState(false);

  useEffect(() => {
    if (value > prevValue) {
      setJustIncreased(true);
      const timer = setTimeout(() => setJustIncreased(false), 300);
      setPrevValue(value);
      return () => clearTimeout(timer);
    }
    if (value < prevValue) {
      setPrevValue(value);
    }
  }, [value, prevValue]);

  // Dynamic Styles based on Intensity
  // Spin duration decreases as intensity increases (12s -> 0.5s)
  const spinDuration = Math.max(0.5, 12 - (intensity / 8)) + 's';
  const reverseSpinDuration = Math.max(0.5, 7 - (intensity / 12)) + 's';
  
  // Pulse duration decreases (2s -> 0.1s)
  const pulseDuration = Math.max(0.1, 2 - (intensity / 40)) + 's';

  // Shake intensity (only starts after 50%)
  const shakeClass = isCritical ? 'animate-violent-shake' : (isHigh ? 'animate-shake' : '');

  return (
    <div className={`relative flex flex-col items-center justify-center transition-all duration-1000 mb-6 ${isExploding ? 'scale-110 z-50' : ''}`}>
      <h3 className={`text-xs font-playfair tracking-[0.2em] mb-2 uppercase transition-colors duration-300 ${isCritical ? 'text-pink-400 animate-pulse font-bold' : 'text-cyan-200/70'}`}>
        {isCritical ? 'CRITICAL RESONANCE' : 'Community Echo Pool'}
      </h3>
      
      <div className={`relative w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center ${shakeClass}`}>
        {/* Outer Static Glow Ring (expands with intensity) */}
        <div 
            className={`absolute inset-0 rounded-full blur-md transition-all duration-500 ${coreColor} opacity-20`} 
            style={{ transform: `scale(${1 + (intensity / 100)})` }}
        />

        {/* Ring 1: Clockwise */}
        <div 
            className={`absolute inset-0 rounded-full border-2 border-dashed ${ringColor} animate-[spin_linear_infinite]`} 
            style={{ animationDuration: spinDuration }}
        />
        
        {/* Ring 2: Counter-Clockwise */}
        <div 
            className={`absolute inset-2 rounded-full border border-dotted ${ringColor} animate-[spin_linear_infinite_reverse] opacity-70`} 
            style={{ animationDuration: reverseSpinDuration }}
        />

        {/* Critical Level: Sparks/Lightning */}
        {isCritical && (
            <>
                <div className="absolute inset-[-10%] border border-pink-500/30 rounded-full animate-ping opacity-20" style={{ animationDuration: '0.5s' }} />
                <Zap className="absolute -top-4 -right-4 w-6 h-6 text-yellow-300 animate-bounce" />
                <Zap className="absolute -bottom-4 -left-4 w-6 h-6 text-yellow-300 animate-bounce delay-75" />
                <div className="absolute inset-0 bg-pink-500/10 rounded-full mix-blend-overlay animate-pulse" style={{ animationDuration: '0.1s' }} />
            </>
        )}

        {/* Inner Glow/Blur */}
        <div 
            className={`absolute inset-4 rounded-full blur-3xl bg-current opacity-30 transition-all duration-500 ${coreColor} ${isExploding ? 'animate-ping scale-150' : 'animate-pulse'}`} 
            style={{ animationDuration: pulseDuration }}
        />
        
        {/* Core Icon Container */}
        <div className={`
            relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-slate-900 to-black 
            flex items-center justify-center shadow-[0_0_50px_currentColor] border border-white/10 
            transition-all duration-300 ${coreColor}
            ${isHigh && !isCritical ? 'animate-shake' : ''}
        `}>
           <Gem className={`
               w-10 h-10 sm:w-12 sm:h-12 text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]
               ${intensity > 80 ? 'animate-pulse' : ''}
           `} style={{ animationDuration: pulseDuration }} />
        </div>

        {/* Contribution Animation */}
        {justIncreased && (
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 text-cyan-300 font-bold font-orbitron text-sm animate-fade-up z-20">
            +{contribution}
          </div>
        )}
      </div>

      <div className={`absolute -bottom-6 bg-slate-900/80 backdrop-blur-md border border-white/10 px-6 py-2 rounded-full text-xs font-mono tracking-widest text-slate-300 shadow-xl transition-transform duration-200 ${justIncreased ? 'scale-105 border-cyan-400/50 text-white' : 'scale-100'}`}>
        <span className={`font-bold text-lg font-orbitron flex items-center gap-2 ${justIncreased ? 'text-cyan-200' : 'text-white'}`}>
            <Gem size={14} className="text-cyan-400" />
            {value.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const PaytableModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in zoom-in-95 duration-200">
      <div className="w-full max-w-md bg-slate-900/90 border border-cyan-500/30 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(6,182,212,0.1)] max-h-[85vh] flex flex-col">
        <div className="bg-slate-950/80 p-6 border-b border-white/10 flex justify-between items-center backdrop-blur-md sticky top-0 z-10">
           <h3 className="font-playfair font-bold text-cyan-400 tracking-wider text-xl italic">Echo Resonance</h3>
           <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto custom-scrollbar flex-grow bg-slate-900/50">
           {SYMBOL_KEYS.map((key) => {
             const s = SYMBOLS[key];
             const Icon = s.icon;
             return (
               <div key={key} className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg bg-black/30 ${s.color}`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <div className="flex flex-col">
                      <span className={`font-playfair font-bold text-base ${s.color}`}>{key}</span>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">{s.description}</span>
                    </div>
                  </div>
                  <div className="text-right">
                     <span className="block font-orbitron font-bold text-white text-lg">
                       {s.value > 200 ? 'JACKPOT' : `${s.value * 0.5}x`}
                     </span>
                     <span className="text-[10px] text-slate-500 uppercase">Payout</span>
                  </div>
               </div>
             )
           })}
        </div>
        <div className="p-4 bg-slate-950/80 text-center text-xs text-slate-500 border-t border-white/10 backdrop-blur-md sticky bottom-0 font-playfair italic">
           Align 3 symbols on any of the 5 cosmic lines to resonate.
        </div>
      </div>
    </div>
  )
}

const HistoryModal = ({ isOpen, onClose }) => {
  const { db, appId } = useAppContext();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!isOpen || !db || !appId) return;
    
    const q = query(
        collection(db, `artifacts/${appId}/public/data/slots_history`),
        orderBy('timestamp', 'desc'),
        limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        setHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [isOpen, db, appId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in zoom-in-95 duration-200">
      <div className="w-full max-w-md bg-slate-900/90 border border-purple-500/30 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(168,85,247,0.1)] max-h-[85vh] flex flex-col">
        <div className="bg-slate-950/80 p-6 border-b border-white/10 flex justify-between items-center backdrop-blur-md sticky top-0 z-10">
           <h3 className="font-playfair font-bold text-purple-400 tracking-wider text-xl italic">Hall of Echoes</h3>
           <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"><X size={24} /></button>
        </div>
        <div className="p-4 space-y-2 overflow-y-auto custom-scrollbar flex-grow bg-slate-900/50">
           {history.length === 0 ? (
               <div className="text-center text-slate-500 py-8 italic">No recent legends found.</div>
           ) : (
               history.map((win) => (
                 <div key={win.id} className={`flex items-center justify-between p-3 rounded-xl border border-white/5 ${win.type === 'JACKPOT' ? 'bg-pink-900/20 border-pink-500/30' : 'bg-white/5 hover:bg-white/10'}`}>
                    <div className="flex items-center gap-3">
                      <img src={win.photoURL || 'https://placehold.co/100x100/AEC6CF/FFFFFF?text=U'} alt={win.displayName} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                      <div className="flex flex-col">
                        <span className={`font-bold text-sm ${win.type === 'JACKPOT' ? 'text-pink-300' : 'text-white'}`}>{win.displayName}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{new Date(win.timestamp?.toDate()).toLocaleTimeString()}</span>
                      </div>
                    </div>
                    <div className="text-right">
                       <span className={`block font-orbitron font-bold text-lg ${win.type === 'JACKPOT' ? 'text-pink-400' : 'text-cyan-300'}`}>
                         +{win.amount?.toLocaleString()}
                       </span>
                       <span className="text-[10px] text-slate-500 uppercase">{win.type === 'JACKPOT' ? 'JACKPOT' : 'BIG WIN'}</span>
                    </div>
                 </div>
               ))
           )}
        </div>
        <div className="p-4 bg-slate-950/80 text-center text-xs text-slate-500 border-t border-white/10 backdrop-blur-md sticky bottom-0 font-playfair italic">
           Fortune favors the resonant.
        </div>
      </div>
    </div>
  );
};

const HarmonicSlots = ({ userBalance, costPerSpin = 50, onSpin, onClose }) => {
  const { db, appId, currentUserProfile } = useAppContext();
  const [currentBet, setCurrentBet] = useState(costPerSpin);
  const [spinning, setSpinning] = useState(false);
  const [exploding, setExploding] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showPaytable, setShowPaytable] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [autoSpinCount, setAutoSpinCount] = useState(0);
  const [showAutoMenu, setShowAutoMenu] = useState(false);
  const [jackpotPool, setJackpotPool] = useState(JACKPOT_SEED); 
  const [jackpotIntensity, setJackpotIntensity] = useState(25);
  const [lastContribution, setLastContribution] = useState(0);
  const [grid, setGrid] = useState([
    ['FLUX', 'PRISM', 'FLUX'],
    ['PRISM', 'ATOM', 'PRISM'],
    ['FLUX', 'FLUX', 'GEM']
  ]);
  const [winData, setWinData] = useState({ amount: 0, type: 'none', lineNames: [] });
  const [displayBalance, setDisplayBalance] = useState(userBalance);
  const [winningCells, setWinningCells] = useState([[], [], []]); 
  const [nearMissCells, setNearMissCells] = useState([[], [], []]);
  const winSoundRef = useRef(null); // Ref for the win sound
  const spinSoundRef = useRef(null); // Ref for the spin sound
  const jackpotSoundRef = useRef(null); // Ref for the jackpot sound

  // Owner Debug State
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const [debugResult, setDebugResult] = useState(null);

  // Listen for global jackpot pool updates
  useEffect(() => {
    if (db && appId) {
      const jackpotRef = doc(db, `artifacts/${appId}/system/harmonicSlots`);
      const unsubscribe = onSnapshot(jackpotRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.pool !== undefined) {
            setJackpotPool(data.pool);
            const newIntensity = Math.min(100, Math.floor((data.pool / JACKPOT_VISUAL_CAP) * 100));
            setJackpotIntensity(newIntensity);
          }
        }
      }, (error) => {
        console.error("Error listening to jackpot pool:", error);
      });
      return () => unsubscribe();
    }
  }, [db, appId]);

  useEffect(() => { setDisplayBalance(userBalance); }, [userBalance]);

  useEffect(() => {
    let timer;
    if (autoSpinCount > 0 && !spinning && !exploding) {
      if (displayBalance < currentBet) { setAutoSpinCount(0); return; }
      timer = setTimeout(() => {
         if (autoSpinCount < 9000) setAutoSpinCount(prev => prev - 1);
         handleSpinClick();
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [autoSpinCount, spinning, exploding, displayBalance, currentBet]);

  const toggleAutoMenu = () => setShowAutoMenu(!showAutoMenu);
  const startAutoSpin = (count) => { setAutoSpinCount(count); setShowAutoMenu(false); if (!spinning) handleSpinClick(); };
  const stopAutoSpin = () => setAutoSpinCount(0);
  const increaseBet = () => { const idx = BET_OPTIONS.indexOf(currentBet); if (idx < BET_OPTIONS.length - 1) setCurrentBet(BET_OPTIONS[idx + 1]); };
  const decreaseBet = () => { const idx = BET_OPTIONS.indexOf(currentBet); if (idx > 0) setCurrentBet(BET_OPTIONS[idx - 1]); };

  const handleSpinClick = async () => {
    if (spinning || exploding || displayBalance < currentBet) return;
    if (soundEnabled) spinSoundRef.current?.play();

    // Optimistically update jackpot visually for immediate feedback
    const poolContribution = Math.floor(currentBet * 0.15); 
    const newPool = jackpotPool + poolContribution;
    const newIntensity = Math.min(100, Math.floor((newPool / JACKPOT_VISUAL_CAP) * 100));

    setLastContribution(poolContribution);
    setDisplayBalance(prev => prev - currentBet);
    setJackpotPool(newPool);
    setJackpotIntensity(newIntensity);
    setSpinning(true);
    setWinData({ amount: 0, type: 'none', lineNames: [] });
    setWinningCells([[], [], []]);
    setNearMissCells([[], [], []]);

    try {
      // Pass debugResult if set
      const spinResult = await onSpin(currentBet, debugResult);
      
      // Reset debug result after one use
      if (debugResult) setDebugResult(null);

      if (spinResult.success && spinResult.finalGrid) {
          const { newBalance: backendNewBalance, winnings, jackpotTriggered, finalGrid: backendFinalGrid, newJackpotPool } = spinResult;
          setDisplayBalance(backendNewBalance);
          
          if (newJackpotPool !== undefined) {
             setJackpotPool(newJackpotPool);
          }
          
          setGrid(backendFinalGrid);

          let winningLines = [];
          let potentialNearMisses = [];

          PAYLINES.forEach(line => {
              const s1 = backendFinalGrid[line.coords[0][0]][line.coords[0][1]];
              const s2 = backendFinalGrid[line.coords[1][0]][line.coords[1][1]];
              const s3 = backendFinalGrid[line.coords[2][0]][line.coords[2][1]];

              if (s1 === s2 && s2 === s3) {
                  winningLines.push(SYMBOLS[s1].id); 
              } else if (s1 === s2 || s1 === s3 || s2 === s3) {
                  if (s1 === s2) potentialNearMisses.push({ col: line.coords[2][0], row: line.coords[2][1] });
                  if (s1 === s3) potentialNearMisses.push({ col: line.coords[1][0], row: line.coords[1][1] });
                  if (s2 === s3) potentialNearMisses.push({ col: line.coords[0][0], row: line.coords[0][1] });
              }
          });

          const finalReelStopDelay = 600;
          setTimeout(() => {
              setSpinning(false);
              if (jackpotTriggered) {
                  setExploding(true);
                  setWinningCells([[0,1,2],[0,1,2],[0,1,2]]); 
                  setNearMissCells([[], [], []]); 
                  setAutoSpinCount(0); 
                  if (soundEnabled) jackpotSoundRef.current?.play();
                  setTimeout(() => {
                      setWinData({ amount: winnings, type: 'jackpot', lineNames: ['HARMONIC CONVERGENCE'] });
                      setExploding(false);
                  }, 3500);
              } else {
                  const finalAmount = Math.floor(winnings || 0);
                  
                  // Calculate Winning Cells based on backend grid
                  let newWinningCells = [[], [], []];
                  PAYLINES.forEach(line => {
                    const s1 = backendFinalGrid[line.coords[0][0]][line.coords[0][1]];
                    const s2 = backendFinalGrid[line.coords[1][0]][line.coords[1][1]];
                    const s3 = backendFinalGrid[line.coords[2][0]][line.coords[2][1]];
                    if (s1 === s2 && s2 === s3) {
                       newWinningCells[line.coords[0][0]].push(line.coords[0][1]);
                       newWinningCells[line.coords[1][0]].push(line.coords[1][1]);
                       newWinningCells[line.coords[2][0]].push(line.coords[2][1]);
                    }
                  });
                  setWinningCells(newWinningCells);

                  const newNearMissCells = [[], [], []];
                  potentialNearMisses.forEach(coord => {
                     if (!newWinningCells[coord.col].includes(coord.row) && !newNearMissCells[coord.col].includes(coord.row)) {
                        newNearMissCells[coord.col].push(coord.row);
                     }
                  });
                  
                  if (finalAmount < currentBet * 10) {
                      setNearMissCells(newNearMissCells);
                  }

                  if (finalAmount > 0) {
                     if (soundEnabled) winSoundRef.current?.play();
                     const isBigWin = finalAmount >= currentBet * 5;
                     setWinData({ amount: finalAmount, type: isBigWin ? 'big_win' : 'win', lineNames: winningLines });
                  } else {
                      setWinData({ amount: 0, type: 'none', lineNames: [] });
                  }
              }
          }, finalReelStopDelay); 
      } else {
          setSpinning(false);
          console.error("Spin failed.");
      }
    } catch (e) {
      console.error("Spin error", e);
      setSpinning(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-full w-full p-2 sm:p-4 relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#0a0a1a] to-black text-white font-sans">
      <style>{`
        @keyframes spin-blur { 0% { transform: translateY(-10%); opacity: 0.5; } 100% { transform: translateY(10%); opacity: 0.8; } }
        .animate-spin-blur { animation: spin-blur 0.08s infinite linear; }
        @keyframes reel-bounce { 0% { transform: translateY(-5%); } 60% { transform: translateY(2%); } 80% { transform: translateY(-1%); } 100% { transform: translateY(0); } }
        .animate-reel-bounce { animation: reel-bounce 0.4s ease-out forwards; }
        @keyframes nudge { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        .animate-nudge { animation: nudge 0.4s ease-in-out 2; }
        @keyframes pulse-gem-glow { 0%, 100% { box-shadow: 0 0 15px rgba(236, 72, 153, 0.5); } 50% { box-shadow: 0 0 30px rgba(236, 72, 153, 0.9); } }
        .animate-pulse-gem-glow { animation: pulse-gem-glow 2s infinite; }
        @keyframes shake { 0%, 100% { transform: translate(0, 0); } 25% { transform: translate(-2px, 2px); } 50% { transform: translate(2px, -2px); } 75% { transform: translate(-2px, -2px); } }
        .animate-shake { animation: shake 0.2s infinite; }
        @keyframes violent-shake { 
          0% { transform: translate(0, 0) rotate(0deg); } 
          10% { transform: translate(-5px, -5px) rotate(-5deg); } 
          20% { transform: translate(5px, 5px) rotate(5deg); } 
          30% { transform: translate(-5px, 5px) rotate(-5deg); } 
          40% { transform: translate(5px, -5px) rotate(5deg); } 
          50% { transform: translate(-2px, 2px) rotate(0deg); }
          60% { transform: translate(2px, -2px) rotate(0deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        .animate-violent-shake { animation: violent-shake 0.2s infinite; }
        @keyframes fade-up { 0% { transform: translate(-50%, 10px); opacity: 0; } 100% { transform: translate(-50%, -20px); opacity: 1; } }
        .animate-fade-up { animation: fade-up 0.8s ease-out forwards; }
      `}</style>

      {/* Owner Debug Menu */}
      {currentUserProfile?.role === 'owner' && (
        <div className="absolute top-6 left-6 z-50">
            <button 
                onClick={() => setShowDebugMenu(!showDebugMenu)} 
                className="p-2 bg-slate-800/80 rounded-full text-slate-400 hover:text-white transition-colors border border-white/10"
            >
                <Wrench size={20} />
            </button>
            {showDebugMenu && (
                <div className="absolute top-full left-0 mt-2 bg-slate-900 border border-white/10 rounded-lg p-2 w-48 shadow-xl z-50 flex flex-col gap-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-2">Next Spin Result</div>
                    <button 
                        onClick={() => { setDebugResult('JACKPOT'); setShowDebugMenu(false); }}
                        className={`text-xs text-left px-2 py-2 rounded hover:bg-white/10 ${debugResult === 'JACKPOT' ? 'text-cyan-400 bg-cyan-900/20' : 'text-white'}`}
                    >
                        Force Jackpot (GEM)
                    </button>
                    <button 
                        onClick={() => { setDebugResult('BIG_WIN'); setShowDebugMenu(false); }}
                        className={`text-xs text-left px-2 py-2 rounded hover:bg-white/10 ${debugResult === 'BIG_WIN' ? 'text-cyan-400 bg-cyan-900/20' : 'text-white'}`}
                    >
                        Force Big Win (NEBULA)
                    </button>
                    <button 
                        onClick={() => { setDebugResult('MID_WIN'); setShowDebugMenu(false); }}
                        className={`text-xs text-left px-2 py-2 rounded hover:bg-white/10 ${debugResult === 'MID_WIN' ? 'text-cyan-400 bg-cyan-900/20' : 'text-white'}`}
                    >
                        Force Mid Win (ATOM)
                    </button>
                    <button 
                        onClick={() => { setDebugResult('SMALL_WIN'); setShowDebugMenu(false); }}
                        className={`text-xs text-left px-2 py-2 rounded hover:bg-white/10 ${debugResult === 'SMALL_WIN' ? 'text-cyan-400 bg-cyan-900/20' : 'text-white'}`}
                    >
                        Force Small Win (PRISM)
                    </button>
                    <button 
                        onClick={() => { setDebugResult('NEAR_MISS'); setShowDebugMenu(false); }}
                        className={`text-xs text-left px-2 py-2 rounded hover:bg-white/10 ${debugResult === 'NEAR_MISS' ? 'text-cyan-400 bg-cyan-900/20' : 'text-white'}`}
                    >
                        Force Near Miss
                    </button>
                    <button 
                        onClick={() => { setDebugResult(null); setShowDebugMenu(false); }}
                        className="text-xs text-left px-2 py-2 rounded hover:bg-white/10 text-slate-400"
                    >
                        Clear Forced Result
                    </button>
                </div>
            )}
        </div>
      )}

      {exploding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-xl animate-[pulse_0.2s_ease-in-out_infinite]">
           <Confetti width={window.innerWidth} height={window.innerHeight} numberOfPieces={500} recycle={true} gravity={0.2} />
           <div className="absolute inset-0 bg-pink-500/20 mix-blend-screen animate-pulse" />
           <h1 className="relative z-10 text-5xl sm:text-7xl sm:text-9xl font-black text-white italic uppercase tracking-tighter drop-shadow-[0_0_80px_rgba(255,0,128,1)] animate-bounce text-center font-playfair">
             JACKPOT<br/><span className="text-4xl sm:text-6xl not-italic font-orbitron text-cyan-300 mt-4 flex items-center justify-center gap-4"><Gem size={48} /> {winData.amount.toLocaleString()}</span>
           </h1>
        </div>
      )}

      <PaytableModal isOpen={showPaytable} onClose={() => setShowPaytable(false)} />
      <HistoryModal isOpen={showHistory} onClose={() => setShowHistory(false)} />
      <audio ref={winSoundRef} src="https://raw.githubusercontent.com/Jelani0/WhispersOfHarmony/main/coin-dispenser-3-189842.mp3" preload="auto" />
      <audio ref={jackpotSoundRef} src="https://raw.githubusercontent.com/Jelani0/WhispersOfHarmony/refs/heads/main/cracker-sound-261119.mp3" preload="auto" />
      <audio ref={spinSoundRef} src="https://github.com/Jelani0/WhispersOfHarmony/raw/refs/heads/main/money-counting-machine-sfx-406495.mp3" preload="auto" />

      <div className="relative w-full max-w-md sm:max-w-lg bg-slate-900/40 rounded-[2.5rem] shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl flex flex-col">
        <div className="bg-gradient-to-b from-slate-900 to-[#0c1221] p-6 pb-12 sm:pb-16 flex flex-col items-center relative z-10 border-b border-white/5">
             <CommunityPool value={jackpotPool} intensity={jackpotIntensity} isExploding={exploding} contribution={lastContribution} />
        </div>

        <div className="relative bg-[#050510]/95 p-4 sm:p-6 pt-2 flex-1 -mt-10 z-20 rounded-t-[2rem] border-t border-white/10 shadow-[0_-20px_60px_rgba(0,0,0,0.9)]">
            <div className="flex justify-between items-end mb-6 px-2">
                 <div className="flex flex-col">
                   <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Your Echoes</span>
                   <div className="text-2xl sm:text-3xl font-orbitron font-bold text-white tracking-tight flex items-baseline gap-2">
                     <Gem className="text-cyan-500" size={20} />{Math.floor(displayBalance).toLocaleString()}
                   </div>
                 </div>
                 <div className="flex items-center gap-1 sm:gap-3 bg-slate-800/50 rounded-full p-0.5 pr-2 sm:p-1 sm:pr-4 border border-white/10">
                   <div className="flex items-center gap-1">
                       <button onClick={decreaseBet} disabled={currentBet === BET_OPTIONS[0] || spinning} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-all active:scale-90"><Minus size={16} /></button>
                       <div className="flex flex-col items-center w-10 sm:w-14">
                           <span className="text-[8px] text-slate-500 uppercase font-bold">Wager</span>
                           <span className="text-amber-400 font-bold font-orbitron text-base sm:text-lg">{currentBet}</span>
                       </div>
                       <button onClick={increaseBet} disabled={currentBet === BET_OPTIONS[BET_OPTIONS.length - 1] || spinning} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-all active:scale-90"><Plus size={16} /></button>
                   </div>
                   <div className="h-6 w-px bg-white/10 mx-0.5 sm:mx-1" />
                   <button onClick={() => setShowHistory(true)} className="text-slate-400 hover:text-purple-400 transition-colors"><Scroll size={16} /></button>
                   <button onClick={() => setShowPaytable(true)} className="text-slate-400 hover:text-cyan-400 transition-colors"><Info size={16} /></button>
                   <button onClick={() => setSoundEnabled(!soundEnabled)} className="text-slate-400 hover:text-white transition-colors">{soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
                 </div>
            </div>

            {winData.type !== 'none' && !exploding && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none">
                 <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" />
                 <div className="relative z-40 flex flex-col items-center animate-in zoom-in-50 duration-500 ease-out-back">
                    <h2 className="text-6xl sm:text-7xl font-playfair font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-100 to-amber-500 drop-shadow-lg uppercase italic tracking-tighter transform -rotate-3">
                      {winData.type === 'big_win' ? 'MEGA WIN' : 'RESONANCE'}
                    </h2>
                    <div className="text-5xl font-orbitron font-bold text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.5)] mt-2 flex items-center gap-2">
                        <Gem className="text-yellow-400" size={32} />
                        +{winData.amount}
                    </div>
                 </div>
              </div>
            )}

            <div className="relative grid grid-cols-3 gap-2 h-64 sm:h-80 bg-slate-900/30 rounded-2xl border border-white/5 shadow-[inset_0_0_30px_rgba(0,0,0,0.8)] p-2">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-white/10 w-full z-0 pointer-events-none opacity-30" />
              <div className="relative h-full z-10"><Reel symbols={grid[0]} isSpinning={spinning} stopDelay={0} highlightIndices={winningCells[0]} nearMissIndices={nearMissCells[0]} hasWin={winData.type !== 'none'} /></div>
              <div className="relative h-full z-10"><Reel symbols={grid[1]} isSpinning={spinning} stopDelay={150} highlightIndices={winningCells[1]} nearMissIndices={nearMissCells[1]} hasWin={winData.type !== 'none'} /></div>
              <div className="relative h-full z-10"><Reel symbols={grid[2]} isSpinning={spinning} stopDelay={300} highlightIndices={winningCells[2]} nearMissIndices={nearMissCells[2]} hasWin={winData.type !== 'none'} /></div>
            </div>
        </div>

        <div className="bg-[#020205] p-4 pt-6 pb-8 border-t border-white/10 relative z-30">
          {showAutoMenu && (
            <div className="absolute bottom-full left-4 mb-2 z-50 flex flex-col gap-1 p-2 bg-slate-800/90 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl min-w-[140px]">
               <div className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider text-center mb-2 pt-1">Auto Spins</div>
               {[10, 25, 50, 9999].map((count) => (
                 <button key={count} onClick={() => startAutoSpin(count)} className="flex items-center justify-between px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all text-sm font-mono text-white">
                    <span>{count === 9999 ? '∞' : count}</span>
                    {count === 9999 && <InfinityIcon size={14} className="text-cyan-400" />}
                 </button>
               ))}
            </div>
          )}

          <div className="flex gap-3 h-16 sm:h-20">
            <button onClick={toggleAutoMenu} className={`h-full aspect-square rounded-2xl bg-slate-800 border border-white/5 hover:border-cyan-500/50 hover:bg-slate-700 transition-all flex flex-col items-center justify-center gap-1 group ${autoSpinCount > 0 ? 'border-red-500/50 bg-red-900/20' : ''}`}>
               {autoSpinCount > 0 ? <StopCircle className="text-red-400 animate-pulse" /> : <Repeat className="text-slate-400 group-hover:text-cyan-400 transition-colors" />}
               <span className={`text-[9px] font-bold uppercase ${autoSpinCount > 0 ? 'text-red-400' : 'text-slate-500 group-hover:text-cyan-400'}`}>{autoSpinCount > 0 ? 'Stop' : 'Auto'}</span>
            </button>

            <button
              onClick={autoSpinCount > 0 ? stopAutoSpin : handleSpinClick}
              disabled={!autoSpinCount && (spinning || exploding || displayBalance < currentBet)}
              className={`
                relative flex-1 h-full rounded-2xl overflow-hidden group shadow-lg
                transition-all duration-200 transform active:scale-[0.98]
                ${autoSpinCount > 0 ? 'bg-slate-800 border border-red-500/30' : 'bg-gradient-to-b from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 shadow-purple-500/20'}
                ${(!autoSpinCount && (spinning || displayBalance < currentBet)) ? 'opacity-50 grayscale cursor-not-allowed' : ''}
              `}
            >
              <div className="absolute inset-0 bg-white/20 group-hover:bg-white/30 transition-colors" />
              {autoSpinCount > 0 ? (
                 <div className="relative z-10 flex flex-col items-center justify-center h-full">
                    <span className="text-red-400 font-bold tracking-widest text-lg">STOP AUTO</span>
                    <span className="text-xs font-mono text-slate-400">{autoSpinCount >= 9000 ? '∞' : autoSpinCount} LEFT</span>
                 </div>
              ) : (
                 <div className="relative z-10 flex items-center justify-center gap-3 h-full">
                    {spinning ? <Loader2 className="w-8 h-8 text-white animate-spin" /> : (
                      <>
                        <span className="font-playfair font-black text-2xl sm:text-3xl text-white tracking-widest drop-shadow-md">WEAVE</span>
                        <Zap className="w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse" />
                      </>
                    )}
                 </div>
              )}
            </button>
          </div>
        </div>
      </div>
      
      <button onClick={onClose} className="absolute top-6 right-6 z-50 text-white/70 hover:text-white p-3 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md transition-all hover:rotate-90">
        <X size={28} />
      </button>
    </div>
  );
};

export default HarmonicSlots;